import React, { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FlexCol from 'components/Layout/FlexCol'
import ProgressBar from 'components/ProgressBar'
import { VoohTargetRealization } from 'types/campaign'
import './VoohCampaignRealization.scss'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import service from '../service'

import { VoohCampaignForm } from '../../VoohCampaignForm/types'
import FlexRow from '../../../../components/Layout/FlexRow'
import VoohImpressionsFilters from './VoohImpressions/VoohImpressionsFilters/VoohImpressionsFilters'
import VoohImpressionsChart from './VoohImpressions/VoohImpressionsChart/VoohImpressionsChart'
import debounce from 'lodash/debounce'
import DateUtils from '../../../../utils/date'
import { DATE_FORMAT } from '../../../../constant'
import { voohCampaignRealizationActions } from './slice'
import { useParams } from 'react-router-dom'
import { ReactRouterParams } from '../../../../types/various'
import VariableUtils from '../../../../utils/variable'

const VoohCampaignRealization: React.FC = () => {
  const { t } = useTranslation()
  const debounceTime = 2000
  const dispatch = useDispatch()
  const realizationData = useSelector((state: RootState) => state.vooCampaignRealization)
  const startDate = useSelector((state: RootState) => state.voohCampaignForm).form.baseValues
    .startDate
  const endDate = useSelector((state: RootState) => state.voohCampaignForm).form.baseValues.endDate
  const frequency = useSelector((state: RootState) => state.voohCampaignForm).form.detailsValues
    .hourFrequency
  const id = useSelector((state: RootState) => state.voohCampaignForm).form.id
  const { uuid } = useParams<ReactRouterParams>()

  const debouncedFetchRealizationData = useCallback(
    debounce(service.getVoohImpressions, debounceTime),
    []
  )

  const campaignTerm = (
    start: VoohCampaignForm['baseValues']['startDate'],
    end: VoohCampaignForm['baseValues']['endDate']
  ): string =>
    `${DateUtils.parseAndFormat(start, DATE_FORMAT)} - ${DateUtils.parseAndFormat(
      end,
      DATE_FORMAT
    )}`

  useEffect(() => {
    return () => {
      dispatch(voohCampaignRealizationActions.resetRealizationData())
    }
  }, [])

  useEffect(() => {
    if (!realizationData.fetchedChartData) {
      debouncedFetchRealizationData(id, realizationData.filters, uuid)
    }
  }, [realizationData.filters])

  const freqencyCol = (
    frequency: VoohCampaignForm['detailsValues']['hourFrequency']
  ): ReactElement => {
    return (
      <div className='Realization-top__target'>
        {`${t('form.campaign.details.hourFrequency')}: `}
        <span className='Realization--highlight'>{frequency}</span>
      </div>
    )
  }

  const termCol = (startDate: string, endDate: string): ReactElement => (
    <div className='Realization-top__term'>
      {`${t('common.campaignTerm')}: `}

      <span className='Realization--highlight'>{campaignTerm(startDate, endDate)}</span>
    </div>
  )

  const progressCol = (details: VoohTargetRealization): ReactElement => {
    const { completion, durationDays, remainingDays } = details

    return (
      <FlexCol className='Realization__progress'>
        <div className='Realization__summary'>
          <div>
            <span>{`${t('common.targetRealization')}: `}</span>
            <span className='Realization--highlight'>{completion}%</span>
          </div>
          <div>
            <span className={''}>{`${t('campaignDetails.chart.sumOfEmissions')}: `}</span>
            <span className='Realization--highlight'>
              {realizationData.chartData.length
                ? VariableUtils.formatNumber(
                    realizationData.chartData[realizationData.chartData.length - 1]
                      .cumulativeImpressions
                  )
                : 0}
            </span>
          </div>
        </div>
        <div className='Realization__progress__bar'>
          <ProgressBar percent={completion > 100 ? 100 : completion} />
          <div className='Realization__progress__container'>
            <div className='Realization__progress__time'>
              {`${t('campaignDetails.emissionTime')}: ${durationDays} ${t(
                'campaignDetails.realization.days'
              )}`}
            </div>

            <div className='Realization__progress__time'>
              {`${t('common.remaining')}: ${remainingDays} ${t(
                'campaignDetails.realization.days'
              )}`}
            </div>
          </div>
        </div>
      </FlexCol>
    )
  }

  const container = (): ReactElement => (
    <div>
      <FlexRow className='Realization-top'>
        {freqencyCol(frequency)}
        {termCol(startDate, endDate)}
      </FlexRow>
      <FlexRow className='Realization-bottom'>
        {realizationData.targetRealization && progressCol(realizationData.targetRealization)}
      </FlexRow>
      <div className='CampaignDetailsChart'>
        <VoohImpressionsFilters />
        <VoohImpressionsChart />
      </div>
    </div>
  )

  return <div className='CampaignDetailsRealization--border-bottom'>{container()}</div>
}

export default VoohCampaignRealization
